import { Button } from "@/components/flexkit/Button";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from "@/components/podkit/dropdown/DropDown";
import { cn } from "@/components/podkit/lib/cn";
import { toast } from "@/components/podkit/toasts/use-toast";
import { type PlainPrompt } from "@/queries/prompt-queries";
import { formatError } from "@/utils/errors";
import { MoreHorizontal } from "lucide-react";
import { memo, useCallback, useState, type FC } from "react";
import { EditCommandModal } from "@/routes/settings/organization/agents/EditCommandModal";
import { DeleteCommandModal } from "@/routes/settings/organization/agents/DeleteCommandModal";
import { CommandCreatedAt } from "@/routes/settings/organization/agents/CommandCreatedAt";
import { CommandCreatedBy } from "@/routes/settings/organization/agents/CommandCreatedBy";

type Props = {
    command: PlainPrompt;
};

const AgentCommandCardComponent: FC<Props> = ({ command }) => {
    const [showEditModal, setShowEditModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);

    const onCopyCommand = useCallback(async () => {
        try {
            await navigator.clipboard.writeText(`/${command.spec?.command || ""}`);
            toast({
                title: "Command copied to clipboard",
                description: `/${command.spec?.command}`,
            });
        } catch (error) {
            toast({
                title: "Failed to copy command to clipboard",
                description: formatError(error),
            });
        }
    }, [command.spec?.command]);

    const onEdit = useCallback(() => {
        setShowEditModal(true);
    }, []);

    const onDelete = useCallback(() => {
        setShowDeleteModal(true);
    }, []);

    return (
        <>
            <div
                data-testid={`command-card-${command.id}`}
                className={cn(
                    "group flex flex-col gap-4 rounded-xl border-0.5 border-solid border-border-base bg-surface-glass px-5 py-3 text-left transition-all duration-200",
                    "hover:border-border-medium hover:shadow-md",
                )}
            >
                <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0 flex-1">
                        <h3 className="truncate text-lg font-semibold leading-tight text-content-primary">
                            {command.metadata?.name}
                        </h3>
                        {command.spec?.command && (
                            <div className="mb-4 font-mono text-sm text-content-secondary">/{command.spec.command}</div>
                        )}
                        <p className="line-clamp-2 text-sm text-content-secondary">{command.metadata?.description}</p>
                    </div>
                    <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="sm" data-testid={`command-actions-${command.id}`}>
                                <MoreHorizontal className="h-4 w-4" />
                            </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                            <DropdownMenuItem
                                onClick={onCopyCommand}
                                data-tracking-id="copy-agent-command-agent-command-card"
                            >
                                Copy Command
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem onClick={onEdit} data-tracking-id="edit-agent-command-agent-command-card">
                                Edit
                            </DropdownMenuItem>
                            <DropdownMenuItem
                                onClick={onDelete}
                                className="text-content-red"
                                data-tracking-id="delete-agent-command-agent-command-card"
                            >
                                Delete
                            </DropdownMenuItem>
                        </DropdownMenuContent>
                    </DropdownMenu>
                </div>

                <div className="mt-auto flex flex-col">
                    <CommandCreatedAt command={command} />
                    <CommandCreatedBy command={command} />
                </div>
            </div>

            {showEditModal && <EditCommandModal command={command} onClose={() => setShowEditModal(false)} />}
            {showDeleteModal && <DeleteCommandModal command={command} onClose={() => setShowDeleteModal(false)} />}
        </>
    );
};

export const AgentCommandCard = memo(AgentCommandCardComponent);
